import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send } from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  useConsultationEmail,
  useSaveConsultationEmail,
  useSendConsultationEmail,
} from "../api/hooks";
import { apiErrorDetail } from "../lib/errors";
import { RichTextEditor } from "./RichTextEditor";

/**
 * Per-consultation email composer: loads the group's saved template (or the
 * localized default with this consultation's survey link), lets the enterprise
 * edit the body + recipients, then Save or Send (recipients BCC'd). Mirrors the
 * legacy consultation email modal.
 */
export function ConsultationEmailEditor({
  consultationId,
  onSent,
}: {
  consultationId: string;
  onSent?: () => void;
}) {
  const { t } = useTranslation();
  const email = useConsultationEmail(consultationId);
  const save = useSaveConsultationEmail(consultationId);
  const send = useSendConsultationEmail(consultationId);

  const [html, setHtml] = useState("");
  const [recipients, setRecipients] = useState("");
  const [loaded, setLoaded] = useState(false);

  // Seed local state once the saved template arrives.
  useEffect(() => {
    if (email.data && !loaded) {
      setHtml(email.data.html);
      setRecipients(email.data.recipients.join(", "));
      setLoaded(true);
    }
  }, [email.data, loaded]);

  if (email.isError) {
    return (
      <p className="text-sm text-destructive">
        {t("actionPlans.errors.loadFailed")}
      </p>
    );
  }
  // Wait until the saved template has seeded local state — the RichTextEditor
  // seeds its DOM once on mount, so it must not mount before `html` is ready.
  if (!loaded) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("actionPlans.consultations.emailLoading")}
      </p>
    );
  }

  const onSave = () =>
    save.mutate(
      { html, recipients },
      {
        onSuccess: () => toast.success(t("actionPlans.consultations.emailSaved")),
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed")),
      },
    );

  const onSend = () =>
    send.mutate(
      { html, recipients },
      {
        onSuccess: (data) => {
          toast.success(
            t("actionPlans.consultations.emailSent", { count: data.sent_count }),
          );
          onSent?.();
        },
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed")),
      },
    );

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div className="space-y-1.5">
        <label
          htmlFor={`recipients-${consultationId}`}
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {t("actionPlans.consultations.recipients_label")}
        </label>
        <input
          id={`recipients-${consultationId}`}
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder={t("actionPlans.consultations.recipientsPlaceholder")}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("actionPlans.consultations.emailBody")}
        </span>
        <RichTextEditor
          value={html}
          onChange={setHtml}
          ariaLabel={t("actionPlans.consultations.emailBody")}
        />
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onSave} disabled={save.isPending}>
          {t("actionPlans.consultations.emailSave")}
        </Button>
        <Button size="sm" onClick={onSend} disabled={send.isPending}>
          <Send aria-hidden />
          {t("actionPlans.consultations.emailSend")}
        </Button>
      </div>
    </div>
  );
}
